import { useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiMail, FiRefreshCw } from "react-icons/fi";
import api from "../../api/api";

const CODE_LENGTH = 6;

/**
 * Shared "enter your 6-digit code" UI used by both the standalone /verify-email
 * page and the Signup "verify" stage. Renders the icon header, code input,
 * verify button, and resend control — but NOT a page/card shell, so each caller
 * can place it inside its own layout.
 *
 * @param {string} email - address the code was sent to
 * @param {() => void} onVerified - called after a successful verification POST
 *   (caller decides what happens next: navigate, advance a stage, etc.)
 * @param {React.ReactNode} [description] - optional copy shown under the form
 * @param {string} [verifyEndpoint] - POST target for verifying the code
 *   (defaults to the signup email-verification endpoint)
 * @param {string} [resendEndpoint] - POST target for resending the code
 *   (defaults to the signup resend endpoint)
 * @param {() => void} [onChangeEmail] - shows "Wrong email? Go back" under the
 *   address (the organization sign-up pages go back to their form)
 */
export default function VerifyCodeForm({
  email,
  onVerified,
  description,
  verifyEndpoint = "/auth/verify-email",
  resendEndpoint = "/auth/resend-verification",
  onChangeEmail,
}) {
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [status, setStatus] = useState("idle"); // idle | verifying | error
  const [error, setError] = useState("");
  const [resendStatus, setResendStatus] = useState("idle"); // idle | sending | sent | error
  const inputRefs = useRef([]);

  const code = digits.join("");
  const canResend = useMemo(() => Boolean(email), [email]);

  const focusBox = (index) => {
    inputRefs.current[index]?.focus();
    inputRefs.current[index]?.select();
  };

  const setDigitAt = (index, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleDigitChange = (index, e) => {
    // Only ever keep the single most-recently-typed numeral in this box.
    const digit = e.target.value.replace(/\D/g, "").slice(-1);
    setDigitAt(index, digit);
    if (status === "error") setStatus("idle");
    if (digit && index < CODE_LENGTH - 1) focusBox(index + 1);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        setDigitAt(index, "");
      } else if (index > 0) {
        setDigitAt(index - 1, "");
        focusBox(index - 1);
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusBox(index - 1);
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      focusBox(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    if (status === "error") setStatus("idle");
    focusBox(Math.min(pasted.length, CODE_LENGTH - 1));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Missing email address to verify.");
      setStatus("error");
      return;
    }
    if (code.length !== CODE_LENGTH) {
      setError("Enter the 6-digit code from your email.");
      setStatus("error");
      return;
    }
    try {
      setStatus("verifying");
      setError("");
      //old code
      //await api.post(verifyEndpoint, { email, code }, { withCredentials: true });
      const res = await api.post(verifyEndpoint, { email, code }, { withCredentials: true });
      // Backend sets pending_client_id so stage2 can finish signup.
      //old code
      //onVerified();
      // The response goes along (/verify-email uses signupComplete)
      onVerified(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || "Invalid or expired code.");
      setStatus("error");
    }
  };

  const resend = async () => {
    if (!canResend || resendStatus === "sending") return;
    try {
      setResendStatus("sending");
      await api.post(resendEndpoint, { email });
      setResendStatus("sent");
      setDigits(Array(CODE_LENGTH).fill(""));
      focusBox(0);
    } catch {
      setResendStatus("error");
    }
  };

  return (
    <div className="text-center">
      <div className="mx-auto mb-5 w-16 h-16 rounded-2xl flex items-center justify-center bg-primary-500/10">
        {status === "error" ? (
          <FiAlertTriangle className="text-amber-600" size={28} />
        ) : (
          <FiMail className="text-primary-600" size={28} />
        )}
      </div>

      <h1 className="text-3xl font-extrabold text-slate-900 mb-2">Enter your code</h1>
      <p className="text-slate-600 mb-1">We sent a 6-digit verification code to:</p>
      <p className="font-mono font-bold text-primary-600 mb-2">{email}</p>
      {onChangeEmail && (
        <button
          type="button"
          onClick={onChangeEmail}
          className="mb-2 text-sm font-semibold text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors"
        >
          Wrong email? Go back
        </button>
      )}
      <p className="mx-auto max-w-sm text-sm text-slate-500 mb-6">
        (Please check your inbox for your Pono Pilot Verification Code. If you can't see it, please check your SPAM folder.)
      </p>

      <form onSubmit={submit}>
        <div className="flex justify-center gap-2 sm:gap-3">
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              autoFocus={index === 0}
              aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
              aria-invalid={status === "error"}
              aria-describedby={status === "error" ? "verification-code-error" : undefined}
              className={`block w-12 h-14 sm:w-14 sm:h-16 rounded-xl border text-center font-mono text-3xl text-slate-900 transition-colors ${
                status === "error" ? "border-red-500" : "border-slate-300"
              }`}
            />
          ))}
        </div>
        {status === "error" && (
          <p id="verification-code-error" className="text-red-500 text-sm mt-3 font-medium">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={status === "verifying" || code.length !== CODE_LENGTH}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 py-3 text-white font-semibold hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "verifying" ? <FiRefreshCw className="animate-spin" /> : null}
          {status === "verifying" ? "Verifying…" : "Verify and continue"}
        </button>
      </form>

      {description && <p className="mt-6 text-sm text-slate-500">{description}</p>}

      <button
        type="button"
        onClick={resend}
        disabled={!canResend || resendStatus === "sending"}
        className="mt-6 inline-flex items-center justify-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {resendStatus === "sending" ? <FiRefreshCw className="animate-spin" size={16} /> : <FiRefreshCw size={16} />}
        {resendStatus === "sent"
          ? "New code sent"
          : resendStatus === "error"
            ? "Resend failed — try again"
            : "Didn’t get it? Resend code"}
      </button>

      <p className="mt-6 text-xs text-slate-500">Code expires in 15 minutes.</p>
    </div>
  );
}
